/**
 * Whether a withdraw's numbers can be saved as stock going OUT.
 *
 * Backend turns each line's quantity into the stock movement by negating
 * it, so a line typed as -10 used to be recorded as ten units coming IN
 * while ten left the shelf (2026-09-14, reproduced by the owner by typing
 * -1 into the quantity cell of the line table, which checked nothing).
 * Backend now refuses these with a 400; this is the same rule on the page,
 * so the operator hears it before anything is sent.
 *
 * Mirrors `validate_amounts` in the Backend withdrawService: plain ASCII
 * numbers only, never negative, the line quantity a whole number. Zero is
 * still accepted -- whether a 0-quantity line is a real case is a business
 * question this fix does not answer. A missing weight or cost is left to
 * the existing form checks, exactly as before.
 */

const NUMBER = /^-?[0-9]+(\.[0-9]+)?$/;

export interface AmountLine {
    quantity?: unknown;
    weight?: unknown;
    cost?: unknown;
}

const isMissing = (value: unknown) =>
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

/** The number, `undefined` when nothing was entered, or `null` when it is not a number. */
const parse = (value: unknown): number | null | undefined => {
    if (isMissing(value)) return undefined;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && NUMBER.test(value.trim())) return Number(value.trim());
    return null;
};

/** '' when the value is acceptable, otherwise the sentence to show. */
export const amountProblem = (value: unknown, label: string, where = '', whole = false, required = false): string => {
    const n = parse(value);
    if (n === undefined) return required ? `${label}${where} ต้องเป็นตัวเลข` : '';
    if (n === null) return `${label}${where} ต้องเป็นตัวเลข`;
    if (n < 0) return `${label}${where} ต้องไม่ติดลบ`;
    if (whole && !Number.isInteger(n)) return `${label}${where} ต้องเป็นจำนวนเต็ม`;
    return '';
};

/** The first problem on one line, or ''. */
export const withdrawLineProblem = (line: AmountLine, where = ''): string =>
    amountProblem(line.quantity, 'จำนวนเบิก', where, true, true) ||
    amountProblem(line.weight, 'น้ำหนัก', where) ||
    amountProblem(line.cost, 'ต้นทุน', where);

/** The first problem across all lines, naming the line, or ''. */
export const withdrawAmountProblem = (lines: AmountLine[] | undefined | null): string => {
    const list = lines || [];
    for (let i = 0; i < list.length; i++) {
        const problem = withdrawLineProblem(list[i] || {}, ` (รายการที่ ${i + 1})`);
        if (problem) return problem;
    }
    return '';
};
